
import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PreferenceScopeEnum,
  ViewConfig,
  SavedView,
  ProjectTaskViewPreferences,
} from '@scrambl/shared';
import { projectSettingsApi } from '@/lib/api/project-settings.api';
import { useAuth } from '@/store/auth.store';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/useProjectTaskViews');

interface UseProjectTaskViewsOptions {
  projectId: string;
  canManage?: boolean;
  enabled?: boolean;
}

interface UseProjectTaskViewsResult {
  views: SavedView[];
  userViews: SavedView[];
  sharedViews: SavedView[];
  activeViewId?: string;
  activeView?: SavedView;
  defaultViewId?: string;
  isLoading: boolean;
  setActiveViewId: (viewId: string) => Promise<void>;
  // The trailing `query` argument exists so this hook stays interchangeable
  // with useModuleViews in ViewSelector. Project task views have no query bar,
  // so it is accepted and ignored.
  saveActiveView: (config: ViewConfig, query?: string | null) => Promise<void>;
  createView: (
    name: string,
    config: ViewConfig,
    scope?: PreferenceScopeEnum,
    query?: string | null,
  ) => Promise<void>;
  setDefaultViewId: (viewId: string) => Promise<void>;
  deleteView: (viewId: string) => Promise<void>;
}

const createViewId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `view_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const getEmptyViewPreferences = (): ProjectTaskViewPreferences => ({
  userViews: {},
  sharedViews: [],
  activeViewIds: {},
  lastUpdatedAt: new Date().toISOString(),
});

/**
 * Project-scoped task views hook
 *
 * Manages saved views (personal and shared) at the project level.
 * Views are stored in project settings alongside column preferences.
 */
export function useProjectTaskViews({
  projectId,
  canManage = false,
  enabled = true,
}: UseProjectTaskViewsOptions): UseProjectTaskViewsResult {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;

  // Fetch project settings to get task views
  const {
    data: projectSettings,
    isLoading,
  } = useQuery({
    queryKey: ['project-settings', projectId],
    queryFn: () => projectSettingsApi.getSettings(projectId),
    enabled: enabled && !!projectId,
  });

  // Extract views from project settings
  const taskViews = projectSettings?.taskViews || getEmptyViewPreferences();

  // Get user's personal views
  const userViews = useMemo(() => {
    if (!userId) return [];
    return taskViews.userViews[userId] || [];
  }, [taskViews.userViews, userId]);

  // Get shared views
  const sharedViews = useMemo(() => {
    return taskViews.sharedViews || [];
  }, [taskViews.sharedViews]);

  // Combine all views (shared first, then personal)
  const views = useMemo(() => [...sharedViews, ...userViews], [sharedViews, userViews]);

  // Get active view ID for current user. There is deliberately no fallback to
  // the first view: nothing applies a view's config on mount, so treating an
  // arbitrary view as active labelled the table with a view it was not showing
  // and flagged it "Edited" against a config the user never chose.
  const activeViewId = useMemo(() => {
    if (!userId) return taskViews.defaultViewId;
    return taskViews.activeViewIds[userId] || taskViews.defaultViewId;
  }, [taskViews.activeViewIds, taskViews.defaultViewId, userId]);

  // Get the active view object
  const activeView = useMemo(
    () => views.find(view => view.id === activeViewId),
    [views, activeViewId],
  );

  const defaultViewId = taskViews.defaultViewId;

  // Refresh queries
  const refreshQueries = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['project-settings', projectId] });
  }, [queryClient, projectId]);

  // Save updated task views to project settings
  const saveTaskViews = useCallback(
    async (updatedViews: ProjectTaskViewPreferences) => {
      const currentSettings = await projectSettingsApi.getSettings(projectId);
      await projectSettingsApi.updateSettings(projectId, {
        ...currentSettings,
        taskViews: {
          ...updatedViews,
          lastUpdatedAt: new Date().toISOString(),
        },
      });
      await refreshQueries();
    },
    [projectId, refreshQueries],
  );

  // Set active view ID for current user
  const setActiveViewId = useCallback(
    async (viewId: string) => {
      if (!userId) return;
      try {
        const updatedViews: ProjectTaskViewPreferences = {
          ...taskViews,
          activeViewIds: {
            ...taskViews.activeViewIds,
            [userId]: viewId,
          },
        };
        await saveTaskViews(updatedViews);
      } catch (error) {
        logger.error('[useProjectTaskViews] Failed to set active view', error);
        toast.error('Failed to update view selection');
      }
    },
    [saveTaskViews, taskViews, userId],
  );

  // Create a new view
  const createView = useCallback(
    async (name: string, config: ViewConfig, scope: PreferenceScopeEnum = PreferenceScopeEnum.USER) => {
      if (!userId) return;

      const targetScope = scope === PreferenceScopeEnum.ORGANIZATION && canManage
        ? PreferenceScopeEnum.ORGANIZATION
        : PreferenceScopeEnum.USER;

      const now = new Date().toISOString();
      const newView: SavedView = {
        id: createViewId(),
        name: name.trim() || 'New view',
        scope: targetScope,
        config,
        createdAt: now,
        updatedAt: now,
      };

      try {
        let updatedViews: ProjectTaskViewPreferences;

        if (targetScope === PreferenceScopeEnum.ORGANIZATION) {
          // Add to shared views
          updatedViews = {
            ...taskViews,
            sharedViews: [...taskViews.sharedViews, newView],
          };
        } else {
          // Add to user's personal views
          const currentUserViews = taskViews.userViews[userId] || [];
          updatedViews = {
            ...taskViews,
            userViews: {
              ...taskViews.userViews,
              [userId]: [...currentUserViews, newView],
            },
            activeViewIds: {
              ...taskViews.activeViewIds,
              [userId]: newView.id,
            },
          };
        }

        await saveTaskViews(updatedViews);
        toast.success(`View "${name}" created`);
      } catch (error) {
        logger.error('[useProjectTaskViews] Failed to create view', error);
        toast.error('Failed to create view');
      }
    },
    [canManage, saveTaskViews, taskViews, userId],
  );

  // Save changes to the active view
  const saveActiveView = useCallback(
    async (config: ViewConfig) => {
      if (!userId) return;

      const now = new Date().toISOString();

      if (!activeView) {
        await createView('My view', config, PreferenceScopeEnum.USER);
        return;
      }

      const updatedView: SavedView = {
        ...activeView,
        config,
        updatedAt: now,
      };

      try {
        let updatedViews: ProjectTaskViewPreferences;

        if (activeView.scope === PreferenceScopeEnum.ORGANIZATION) {
          if (!canManage) {
            // User can't edit shared views, create a personal copy
            await createView(`My ${activeView.name}`, config, PreferenceScopeEnum.USER);
            return;
          }
          // Update shared view
          updatedViews = {
            ...taskViews,
            sharedViews: taskViews.sharedViews.map(view =>
              view.id === activeView.id ? updatedView : view,
            ),
          };
        } else {
          // Update personal view
          const currentUserViews = taskViews.userViews[userId] || [];
          updatedViews = {
            ...taskViews,
            userViews: {
              ...taskViews.userViews,
              [userId]: currentUserViews.map(view =>
                view.id === activeView.id ? updatedView : view,
              ),
            },
          };
        }

        await saveTaskViews(updatedViews);
      } catch (error) {
        logger.error('[useProjectTaskViews] Failed to save view', error);
        toast.error('Failed to save view');
      }
    },
    [activeView, canManage, createView, saveTaskViews, taskViews, userId],
  );

  // Set default view (shared views only)
  const setDefaultViewId = useCallback(
    async (viewId: string) => {
      if (!canManage) return;
      if (!sharedViews.some(view => view.id === viewId)) return;

      try {
        const updatedViews: ProjectTaskViewPreferences = {
          ...taskViews,
          defaultViewId: viewId,
        };
        await saveTaskViews(updatedViews);
      } catch (error) {
        logger.error('[useProjectTaskViews] Failed to set default view', error);
        toast.error('Failed to update default view');
      }
    },
    [canManage, saveTaskViews, sharedViews, taskViews],
  );

  // Delete a view
  const deleteView = useCallback(
    async (viewId: string) => {
      if (!userId) return;

      const view = views.find(item => item.id === viewId);
      if (!view) return;

      if (view.scope === PreferenceScopeEnum.ORGANIZATION) {
        if (!canManage) {
          toast.error('You do not have permission to delete shared views');
          return;
        }

        try {
          const nextSharedViews = sharedViews.filter(item => item.id !== viewId);
          const nextDefaultViewId =
            taskViews.defaultViewId === viewId
              ? nextSharedViews[0]?.id
              : taskViews.defaultViewId;

          const updatedViews: ProjectTaskViewPreferences = {
            ...taskViews,
            sharedViews: nextSharedViews,
            defaultViewId: nextDefaultViewId,
          };

          // If this was the active view for current user, update active view
          if (taskViews.activeViewIds[userId] === viewId) {
            const nextActiveViewId = nextDefaultViewId || nextSharedViews[0]?.id || userViews[0]?.id;
            if (nextActiveViewId) {
              updatedViews.activeViewIds = {
                ...taskViews.activeViewIds,
                [userId]: nextActiveViewId,
              };
            }
          }

          await saveTaskViews(updatedViews);
          toast.success('Shared view deleted');
        } catch (error) {
          logger.error('[useProjectTaskViews] Failed to delete shared view', error);
          toast.error('Failed to delete shared view');
        }

        return;
      }

      // Delete personal view
      try {
        const currentUserViews = taskViews.userViews[userId] || [];
        const nextUserViews = currentUserViews.filter(item => item.id !== viewId);
        const nextActiveViewId =
          taskViews.activeViewIds[userId] === viewId
            ? taskViews.defaultViewId || sharedViews[0]?.id || nextUserViews[0]?.id
            : taskViews.activeViewIds[userId];

        const updatedViews: ProjectTaskViewPreferences = {
          ...taskViews,
          userViews: {
            ...taskViews.userViews,
            [userId]: nextUserViews,
          },
          activeViewIds: {
            ...taskViews.activeViewIds,
            [userId]: nextActiveViewId || '',
          },
        };

        await saveTaskViews(updatedViews);
        toast.success('View deleted');
      } catch (error) {
        logger.error('[useProjectTaskViews] Failed to delete personal view', error);
        toast.error('Failed to delete view');
      }
    },
    [canManage, saveTaskViews, sharedViews, taskViews, userId, userViews, views],
  );

  return {
    views,
    userViews,
    sharedViews,
    activeViewId,
    activeView,
    defaultViewId,
    isLoading,
    setActiveViewId,
    saveActiveView,
    createView,
    setDefaultViewId,
    deleteView,
  };
}
